import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { CreditCard, Wallet, Loader2, CheckCircle, XCircle, Smartphone, AlertTriangle, FileText, Building2, Globe, Plus, MapPin } from 'lucide-react';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';
import axios from 'axios';
import { useCart } from '../context/CartContext';
import { DeviceCompatibilityChecker } from './DeviceCompatibility';
import { trackPurchase } from '../utils/analytics';
import { useCurrency } from '../context/CurrencyContext';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export function CheckoutPage({ onSuccess, onBack, onNavigate }) {
  const { t, i18n } = useTranslation();
  const { cart, cartId, cartTotal, clearCart, addToCart } = useCart();
  const { formatPrice, currency } = useCurrency();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('stripe');
  const [paypalClientId, setPaypalClientId] = useState(null);
  const [paypalProcessing, setPaypalProcessing] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [couponCode, setCouponCode] = useState('');
  const [couponLoading, setCouponLoading] = useState(false);
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [couponError, setCouponError] = useState('');
  const [invoiceRequested, setInvoiceRequested] = useState(false);
  const [invoiceEntity, setInvoiceEntity] = useState('eu');
  const [upsellProducts, setUpsellProducts] = useState([]);
  const [upsellLoading, setUpsellLoading] = useState(false);
  const [addingUpsell, setAddingUpsell] = useState('');
  const [invoiceBilling, setInvoiceBilling] = useState({
    company_name: '',
    address: '',
    city: '',
    postal_code: '',
    country: '',
    tax_id: '',
    email: '',
  });

  const discountAmount = appliedCoupon
    ? appliedCoupon.discount_type === 'percent'
      ? cartTotal * (parseFloat(appliedCoupon.amount) / 100)
      : Math.min(parseFloat(appliedCoupon.amount), cartTotal)
    : 0;
  const finalTotal = Math.max(0, cartTotal - discountAmount);

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    country: '',
    postalCode: ''
  });

  useEffect(() => {
    axios.get(`${API}/paypal/client-id`).then(res => {
      setPaypalClientId(res.data.client_id);
    }).catch(() => {});
  }, []);

  // Fetch upsell suggestions based on cart items
  useEffect(() => {
    if (cart.items.length === 0) return;
    const countries = cart.items.map(item => item.name?.replace(/^eSIM\s+(for\s+)?/i, '').split(/\s*[\-\(]/)[0].trim()).filter(Boolean);
    if (countries.length === 0) return;
    setUpsellLoading(true);
    axios.post(`${API}/products/upsell`, { countries })
      .then(res => setUpsellProducts(res.data.suggestions || []))
      .catch(() => {})
      .finally(() => setUpsellLoading(false));
  }, [cart.items]);

  const handleAddUpsell = async (product) => {
    setAddingUpsell(product.id);
    try {
      await addToCart({
        product_id: product.id,
        id: product.id,
        name: product.name,
        price: parseFloat(product.price),
        country_code: product.country_code,
      });
      // Remove from suggestions after adding
      setUpsellProducts(prev => prev.filter(p => p.id !== product.id));
    } catch { /* ignore */ }
    setAddingUpsell('');
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const getBilling = () => ({
    first_name: formData.firstName,
    last_name: formData.lastName,
    email: formData.email,
    phone: formData.phone,
    address_1: formData.address,
    city: formData.city,
    country: formData.country,
    postcode: formData.postalCode
  });

  const handleStripeCheckout = async (e) => {
    e.preventDefault();
    if (paymentMethod !== 'stripe') return;
    setLoading(true);
    setError(null);

    try {
      const response = await axios.post(`${API}/stripe/create-checkout`, {
        cart_id: cartId,
        billing: getBilling(),
        origin_url: window.location.origin,
        coupon_code: appliedCoupon?.code || null,
        invoice_requested: invoiceRequested,
        invoice_entity: invoiceRequested ? invoiceEntity : null,
        invoice_billing_data: invoiceRequested ? { ...invoiceBilling, email: formData.email } : null,
        language: i18n.language || 'en',
      });

      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Payment initialization failed. Please try again.');
      setLoading(false);
    }
  };

  const createPayPalOrder = async () => {
    setError(null);
    const items = cart.items.map(item => ({
      name: item.name,
      price: item.price,
      quantity: item.quantity,
      product_id: item.product_id,
      variation_id: item.variation_id
    }));
    const res = await axios.post(`${API}/paypal/create-order`, {
      items,
      billing: getBilling(),
      coupon_code: appliedCoupon?.code || "",
      invoice_requested: invoiceRequested,
      invoice_entity: invoiceRequested ? invoiceEntity : null,
      invoice_billing_data: invoiceRequested ? { ...invoiceBilling, email: formData.email } : null,
      language: i18n.language || 'en',
    });
    return res.data.id;
  };

  const onPayPalApprove = async (data) => {
    setPaypalProcessing(true);
    setError(null);
    try {
      const res = await axios.post(`${API}/paypal/capture-order/${data.orderID}`);
      if (res.data.status === 'COMPLETED') {
        clearCart();
        onSuccess?.('paypal', data.orderID);
      }
    } catch (err) {
      setError('PayPal payment capture failed. Please try again.');
    } finally {
      setPaypalProcessing(false);
    }
  };

  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) return;
    setCouponLoading(true);
    setCouponError('');
    try {
      const res = await axios.post(`${API}/coupons/validate`, { code: couponCode.trim() });
      setAppliedCoupon(res.data);
    } catch (err) {
      setCouponError(err.response?.data?.detail || 'Invalid coupon');
      setAppliedCoupon(null);
    } finally {
      setCouponLoading(false);
    }
  };

  const inputClass = "w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:border-teal-500 transition-colors";

  return (
    <div className="min-h-screen bg-[#0f1419] pt-24 pb-16">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <button
            onClick={onBack}
            className="text-slate-400 hover:text-white mb-8 flex items-center space-x-2 transition-colors"
            data-testid="back-button"
          >
            <span>&larr; {t('common.back')}</span>
          </button>

          <h1 className="text-3xl font-bold text-white mb-8 font-outfit" data-testid="checkout-title">
            {t('checkout.title')}
          </h1>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              <form onSubmit={handleStripeCheckout} className="space-y-8">
                {/* Billing */}
                <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6">
                  <h2 className="text-lg font-semibold text-white mb-6">{t('checkout.billing')}</h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm text-slate-400 mb-2">{t('checkout.firstName')}</label>
                      <input type="text" name="firstName" value={formData.firstName} onChange={handleChange} required className={inputClass} data-testid="input-firstName" />
                    </div>
                    <div>
                      <label className="block text-sm text-slate-400 mb-2">{t('checkout.lastName')}</label>
                      <input type="text" name="lastName" value={formData.lastName} onChange={handleChange} required className={inputClass} data-testid="input-lastName" />
                    </div>
                    <div>
                      <label className="block text-sm text-slate-400 mb-2">{t('checkout.email')}</label>
                      <input type="email" name="email" value={formData.email} onChange={handleChange} required className={inputClass} data-testid="input-email" />
                    </div>
                    <div>
                      <label className="block text-sm text-slate-400 mb-2">{t('checkout.phone')}</label>
                      <input type="tel" name="phone" value={formData.phone} onChange={handleChange} className={inputClass} data-testid="input-phone" />
                    </div>
                    <div className="sm:col-span-2">
                      <label className="block text-sm text-slate-400 mb-2">{t('checkout.address')}</label>
                      <input type="text" name="address" value={formData.address} onChange={handleChange} className={inputClass} data-testid="input-address" />
                    </div>
                    <div>
                      <label className="block text-sm text-slate-400 mb-2">{t('checkout.city')}</label>
                      <input type="text" name="city" value={formData.city} onChange={handleChange} className={inputClass} data-testid="input-city" />
                    </div>
                    <div>
                      <label className="block text-sm text-slate-400 mb-2">{t('checkout.country')}</label>
                      <input type="text" name="country" value={formData.country} onChange={handleChange} className={inputClass} data-testid="input-country" />
                    </div>
                    <div>
                      <label className="block text-sm text-slate-400 mb-2">{t('checkout.postalCode')}</label>
                      <input type="text" name="postalCode" value={formData.postalCode} onChange={handleChange} className={inputClass} data-testid="input-postalCode" />
                    </div>
                  </div>
                </div>

                {/* Invoice Request */}
                <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6" data-testid="invoice-section">
                  <label className="flex items-center gap-3 cursor-pointer group" data-testid="invoice-toggle">
                    <input
                      type="checkbox"
                      checked={invoiceRequested}
                      onChange={(e) => setInvoiceRequested(e.target.checked)}
                      className="w-4 h-4 rounded border-white/20 bg-white/5 text-teal-500 focus:ring-teal-500"
                    />
                    <div className="flex items-center gap-2">
                      <FileText className="w-5 h-5 text-teal-400" />
                      <span className="text-white font-medium">{t('checkout.needInvoice', 'Need an invoice?')}</span>
                    </div>
                  </label>

                  {invoiceRequested && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      className="mt-6 space-y-5"
                    >
                      {/* Entity Selection */}
                      <div>
                        <label className="block text-sm text-slate-400 mb-3">{t('checkout.invoiceEntity', 'Invoice from:')}</label>
                        <div className="grid grid-cols-2 gap-3">
                          <button
                            type="button"
                            onClick={() => setInvoiceEntity('eu')}
                            className={`p-4 rounded-xl border transition-all duration-200 flex flex-col items-center gap-2 ${
                              invoiceEntity === 'eu'
                                ? 'bg-teal-500/20 border-teal-500 text-teal-400'
                                : 'bg-white/5 border-white/10 text-slate-400 hover:bg-white/10'
                            }`}
                            data-testid="invoice-entity-eu"
                          >
                            <Building2 className="w-5 h-5" />
                            <span className="text-sm font-medium">EU (Poland)</span>
                            <span className="text-xs opacity-60">TelcaVoIP Int. EU</span>
                          </button>
                          <button
                            type="button"
                            onClick={() => setInvoiceEntity('us')}
                            className={`p-4 rounded-xl border transition-all duration-200 flex flex-col items-center gap-2 ${
                              invoiceEntity === 'us'
                                ? 'bg-teal-500/20 border-teal-500 text-teal-400'
                                : 'bg-white/5 border-white/10 text-slate-400 hover:bg-white/10'
                            }`}
                            data-testid="invoice-entity-us"
                          >
                            <Globe className="w-5 h-5" />
                            <span className="text-sm font-medium">USA</span>
                            <span className="text-xs opacity-60">TelcaVoIP Int. EU LLC</span>
                          </button>
                        </div>
                      </div>

                      {/* Invoice Billing Data */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="sm:col-span-2">
                          <label className="block text-sm text-slate-400 mb-2">{t('checkout.companyName', 'Company / Full Name')}</label>
                          <input
                            type="text"
                            value={invoiceBilling.company_name}
                            onChange={(e) => setInvoiceBilling(prev => ({ ...prev, company_name: e.target.value }))}
                            required={invoiceRequested}
                            className={inputClass}
                            data-testid="invoice-company-name"
                          />
                        </div>
                        <div className="sm:col-span-2">
                          <label className="block text-sm text-slate-400 mb-2">{t('checkout.invoiceAddress', 'Billing Address')}</label>
                          <input
                            type="text"
                            value={invoiceBilling.address}
                            onChange={(e) => setInvoiceBilling(prev => ({ ...prev, address: e.target.value }))}
                            required={invoiceRequested}
                            className={inputClass}
                            data-testid="invoice-address"
                          />
                        </div>
                        <div>
                          <label className="block text-sm text-slate-400 mb-2">{t('checkout.city')}</label>
                          <input
                            type="text"
                            value={invoiceBilling.city}
                            onChange={(e) => setInvoiceBilling(prev => ({ ...prev, city: e.target.value }))}
                            required={invoiceRequested}
                            className={inputClass}
                            data-testid="invoice-city"
                          />
                        </div>
                        <div>
                          <label className="block text-sm text-slate-400 mb-2">{t('checkout.postalCode')}</label>
                          <input
                            type="text"
                            value={invoiceBilling.postal_code}
                            onChange={(e) => setInvoiceBilling(prev => ({ ...prev, postal_code: e.target.value }))}
                            required={invoiceRequested}
                            className={inputClass}
                            data-testid="invoice-postal-code"
                          />
                        </div>
                        <div>
                          <label className="block text-sm text-slate-400 mb-2">{t('checkout.country')}</label>
                          <input
                            type="text"
                            value={invoiceBilling.country}
                            onChange={(e) => setInvoiceBilling(prev => ({ ...prev, country: e.target.value }))}
                            required={invoiceRequested}
                            className={inputClass}
                            data-testid="invoice-country"
                          />
                        </div>
                        <div>
                          <label className="block text-sm text-slate-400 mb-2">
                            {invoiceEntity === 'eu' ? 'VAT Number' : 'Tax ID'} <span className="text-slate-600">({t('common.optional', 'optional')})</span>
                          </label>
                          <input
                            type="text"
                            value={invoiceBilling.tax_id}
                            onChange={(e) => setInvoiceBilling(prev => ({ ...prev, tax_id: e.target.value }))}
                            placeholder={invoiceEntity === 'eu' ? 'e.g. IT01234567890' : 'e.g. 12-3456789'}
                            className={inputClass}
                            data-testid="invoice-tax-id"
                          />
                        </div>
                      </div>
                    </motion.div>
                  )}
                </div>

                {/* Payment Method */}
                <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6">
                  <h2 className="text-lg font-semibold text-white mb-6">{t('checkout.payment')}</h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <button
                      type="button"
                      onClick={() => setPaymentMethod('stripe')}
                      className={`p-4 rounded-xl border transition-all duration-200 flex flex-col items-center space-y-2 ${
                        paymentMethod === 'stripe'
                          ? 'bg-teal-500/20 border-teal-500 text-teal-400'
                          : 'bg-white/5 border-white/10 text-slate-400 hover:bg-white/10'
                      }`}
                      data-testid="payment-stripe"
                    >
                      <CreditCard className="w-6 h-6" />
                      <span className="text-sm font-medium">{t('checkout.creditCard')}</span>
                      <span className="text-xs opacity-60">{t('checkout.cardBrands')}</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setPaymentMethod('paypal')}
                      className={`p-4 rounded-xl border flex flex-col items-center space-y-2 transition-all ${
                        paymentMethod === 'paypal'
                          ? 'bg-teal-500/10 border-teal-500/50 text-teal-400'
                          : 'bg-white/5 border-white/10 text-slate-400 hover:bg-white/10'
                      }`}
                      data-testid="payment-paypal"
                    >
                      <Wallet className="w-6 h-6" />
                      <span className="text-sm font-medium">{t('checkout.paypal')}</span>
                      <span className="text-xs opacity-60">{t('checkout.paypalAccount')}</span>
                    </button>
                  </div>
                </div>

                {/* eSIM Compatibility Reminder */}
                <div className="bg-amber-500/10 border border-amber-500/20 rounded-2xl p-5" data-testid="compatibility-reminder">
                  <div className="flex items-start gap-3 mb-3">
                    <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <h3 className="text-amber-300 font-medium text-sm">{t('checkout.checkDevice')}</h3>
                      <p className="text-slate-400 text-xs mt-1">{t('checkout.checkDeviceDesc')}</p>
                    </div>
                  </div>
                  <DeviceCompatibilityChecker compact={true} />
                </div>

                {/* Terms & Conditions */}
                <label className="flex items-start gap-3 cursor-pointer group" data-testid="terms-checkbox-label">
                  <input
                    type="checkbox"
                    checked={acceptedTerms}
                    onChange={(e) => setAcceptedTerms(e.target.checked)}
                    className="mt-1 w-4 h-4 rounded border-white/20 bg-white/5 text-teal-500 focus:ring-teal-500"
                    data-testid="terms-checkbox"
                    required
                  />
                  <span className="text-slate-400 text-sm leading-relaxed">
                    {t('checkout.acceptTerms')}{' '}
                    <button type="button" onClick={() => onNavigate?.('terms')} className="text-teal-400 hover:underline">{t('footer.terms')}</button>,{' '}
                    <button type="button" onClick={() => onNavigate?.('privacy')} className="text-teal-400 hover:underline">{t('footer.privacy')}</button>
                    {' '}&{' '}
                    <button type="button" onClick={() => onNavigate?.('refund')} className="text-teal-400 hover:underline">{t('footer.refund')}</button>.
                  </span>
                </label>

                {error && (
                  <div className="bg-rose-500/20 border border-rose-500/50 rounded-xl p-4 text-rose-400" data-testid="checkout-error">
                    {error}
                  </div>
                )}

                {paymentMethod === 'stripe' ? (
                  <button
                    type="submit"
                    disabled={loading || cart.items.length === 0 || !acceptedTerms}
                    className="w-full bg-teal-500 hover:bg-teal-400 disabled:bg-slate-700 disabled:cursor-not-allowed text-black font-bold py-4 rounded-full transition-all duration-200 hover:shadow-[0_0_20px_rgba(6,182,212,0.5)] flex items-center justify-center space-x-2"
                    data-testid="place-order-button"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        <span>{t('checkout.processing')}</span>
                      </>
                    ) : (
                      <span>{t('checkout.placeOrder')} - {formatPrice(cartTotal)}</span>
                    )}
                  </button>
                ) : paypalClientId ? (
                  <div data-testid="paypal-buttons-container">
                    {paypalProcessing && (
                      <div className="flex items-center justify-center gap-2 mb-4 text-teal-400">
                        <Loader2 className="w-5 h-5 animate-spin" />
                        <span className="text-sm">{t('checkout.processingPaypal')}</span>
                      </div>
                    )}
                    <PayPalScriptProvider options={{
                      clientId: paypalClientId,
                      currency: "EUR",
                      intent: "capture"
                    }}>
                      <PayPalButtons
                        style={{ layout: "vertical", color: "blue", shape: "pill", label: "pay", height: 48 }}
                        disabled={cart.items.length === 0 || paypalProcessing || !acceptedTerms}
                        createOrder={createPayPalOrder}
                        onApprove={onPayPalApprove}
                        onError={(err) => setError('PayPal error. Please try again.')}
                        onCancel={() => setError(null)}
                      />
                    </PayPalScriptProvider>
                  </div>
                ) : (
                  <div className="text-center text-slate-500 py-4">
                    <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" />
                    <span className="text-sm">Loading PayPal...</span>
                  </div>
                )}
              </form>
            </div>

            {/* Order Summary */}
            <div className="lg:col-span-1">
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 sticky top-24">
                <h3 className="text-lg font-semibold text-white mb-4" data-testid="order-summary-title">{t('checkout.orderSummary')}</h3>
                <div className="space-y-3 mb-6">
                  {cart.items.map(item => (
                    <div key={item.product_id} className="flex justify-between text-sm">
                      <span className="text-slate-400">{item.name} x{item.quantity}</span>
                      <span className="text-white font-mono">{formatPrice(item.price * item.quantity)}</span>
                    </div>
                  ))}
                </div>

                {/* Travel Bundle Upsell */}
                {upsellProducts.length > 0 && (
                  <div className="mb-4" data-testid="upsell-section">
                    <div className="flex items-center gap-1.5 mb-3">
                      <MapPin className="w-3.5 h-3.5 text-amber-400" />
                      <span className="text-amber-400 text-xs font-semibold uppercase tracking-wider">{t('checkout.travelBundle', 'Bundle Viaggio')}</span>
                    </div>
                    <div className="space-y-2">
                      {upsellProducts.map(product => (
                        <div key={product.id} className="flex items-center justify-between bg-amber-500/5 border border-amber-500/10 rounded-lg px-3 py-2" data-testid={`upsell-${product.id}`}>
                          <div className="flex items-center gap-2 min-w-0">
                            {product.flag_url && (
                              <img src={product.flag_url} alt="" className="w-5 h-3.5 rounded-sm object-cover flex-shrink-0" onError={(e) => { e.target.style.display = 'none'; }} />
                            )}
                            <div className="min-w-0">
                              <p className="text-white text-xs font-medium truncate">{product.country_name}</p>
                              <p className="text-slate-500 text-[10px]">{t('checkout.upsellFrom', 'da')} {formatPrice(parseFloat(product.price))}</p>
                            </div>
                          </div>
                          <button
                            onClick={() => handleAddUpsell(product)}
                            disabled={addingUpsell === product.id}
                            className="flex items-center gap-1 bg-amber-500/20 hover:bg-amber-500/30 text-amber-400 text-xs font-semibold px-2.5 py-1.5 rounded-lg transition-all flex-shrink-0"
                            data-testid={`upsell-add-${product.id}`}
                          >
                            {addingUpsell === product.id ? (
                              <Loader2 className="w-3 h-3 animate-spin" />
                            ) : (
                              <Plus className="w-3 h-3" />
                            )}
                            {t('checkout.upsellAdd', 'Aggiungi')}
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Coupon Code */}
                <div className="mb-4">
                  {appliedCoupon ? (
                    <div className="flex items-center justify-between bg-emerald-500/10 border border-emerald-500/20 rounded-lg px-3 py-2">
                      <div className="flex items-center gap-2">
                        <span className="text-emerald-400 text-sm font-medium">{appliedCoupon.code}</span>
                        <span className="text-emerald-400/60 text-xs">
                          -{appliedCoupon.discount_type === 'percent' ? `${appliedCoupon.amount}%` : formatPrice(appliedCoupon.amount)}
                        </span>
                      </div>
                      <button onClick={() => { setAppliedCoupon(null); setCouponCode(''); }} className="text-slate-500 hover:text-white text-xs">{t('common.delete')}</button>
                    </div>
                  ) : (
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={couponCode}
                        onChange={(e) => { setCouponCode(e.target.value); setCouponError(''); }}
                        placeholder={t('checkout.couponCode')}
                        className="flex-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-teal-500"
                        data-testid="coupon-input"
                      />
                      <button
                        type="button"
                        onClick={handleApplyCoupon}
                        disabled={couponLoading || !couponCode.trim()}
                        className="bg-white/10 hover:bg-white/15 disabled:opacity-50 text-white text-sm px-4 py-2 rounded-lg transition-colors"
                        data-testid="apply-coupon"
                      >
                        {couponLoading ? '...' : t('checkout.applyCoupon')}
                      </button>
                    </div>
                  )}
                  {couponError && <p className="text-rose-400 text-xs mt-1">{couponError}</p>}
                </div>

                <div className="border-t border-white/10 pt-4 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-400">{t('checkout.subtotal')}</span>
                    <span className="text-slate-300 font-mono">{formatPrice(cartTotal)}</span>
                  </div>
                  {appliedCoupon && (
                    <div className="flex justify-between text-sm">
                      <span className="text-emerald-400">{t('checkout.discount')}</span>
                      <span className="text-emerald-400 font-mono">-{formatPrice(discountAmount)}</span>
                    </div>
                  )}
                  <div className="flex justify-between pt-2 border-t border-white/5">
                    <span className="text-white font-medium">{t('checkout.total', t('cart.total'))}</span>
                    <span className="text-xl font-bold text-white font-mono" data-testid="order-total">{formatPrice(finalTotal)}</span>
                  </div>
                  {currency !== 'EUR' && (
                    <p className="text-amber-400/80 text-xs mt-2 flex items-center gap-1.5" data-testid="eur-payment-note">
                      <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                      {t('checkout.eurPaymentNote', { amount: `€${finalTotal.toFixed(2)}` })}
                    </p>
                  )}
                </div>
                <div className="mt-4 flex items-center space-x-2 text-xs text-slate-500">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg>
          <span>{t('checkout.securePayment')} {paymentMethod === 'paypal' ? 'PayPal' : 'Stripe'}</span>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}

export function PaymentStatus({ sessionId, onContinue, onBack }) {
  const { t } = useTranslation();
  const { clearCart } = useCart();
  const { formatPrice } = useCurrency();
  const [status, setStatus] = useState('checking');
  const [paymentData, setPaymentData] = useState(null);
  const [attempts, setAttempts] = useState(0);

  const pollStatus = useCallback(async () => {
    if (attempts >= 10) {
      setStatus('timeout');
      return;
    }

    try {
      const response = await axios.get(`${API}/stripe/checkout-status/${sessionId}`);
      const data = response.data;
      setPaymentData(data);

      if (data.payment_status === 'paid') {
        setStatus('success');
        trackPurchase(
          sessionId,
          data.amount_total ? data.amount_total / 100 : 0,
          data.items || []
        );
        clearCart();
        return;
      } else if (data.status === 'expired') {
        setStatus('expired');
        return;
      }

      setAttempts(prev => prev + 1);
    } catch {
      setAttempts(prev => prev + 1);
    }
  }, [sessionId, attempts, clearCart]);

  useEffect(() => {
    if (status === 'checking') {
      const timer = setTimeout(pollStatus, attempts === 0 ? 500 : 2000);
      return () => clearTimeout(timer);
    }
  }, [status, attempts, pollStatus]);

  if (status === 'checking') {
    return (
      <div className="min-h-screen bg-[#0f1419] pt-24 pb-16 flex items-center justify-center">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center">
          <Loader2 className="w-16 h-16 text-teal-400 animate-spin mx-auto mb-6" />
          <h2 className="text-2xl font-bold text-white mb-2 font-outfit" data-testid="payment-checking">{t('checkout.verifyingPayment')}</h2>
          <p className="text-slate-400">{t('checkout.verifyingPaymentDesc')}</p>
        </motion.div>
      </div>
    );
  }

  if (status === 'success') {
    return (
      <div className="min-h-screen bg-[#0f1419] pt-24 pb-16 flex items-center justify-center">
        <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="max-w-md mx-auto px-4 text-center">
          <div className="w-20 h-20 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-emerald-400" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-4 font-outfit" data-testid="payment-success">{t('checkout.paymentCompleted')}</h1>
          <p className="text-slate-400 mb-8">{t('checkout.orderConfirmed')}</p>
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 mb-8">
            <p className="text-slate-400 text-sm mb-2">{t('checkout.totalPaid')}</p>
            <p className="text-3xl font-bold text-white font-mono" data-testid="payment-amount">
              {formatPrice(paymentData?.amount_total ? paymentData.amount_total / 100 : 0)}
            </p>
          </div>
          <button
            onClick={onContinue}
            className="bg-teal-500 hover:bg-teal-400 text-black font-bold px-8 py-4 rounded-full transition-all duration-200 hover:shadow-[0_0_20px_rgba(6,182,212,0.5)]"
            data-testid="continue-shopping-button"
          >
            {t('cart.continueShopping')}
          </button>
        </motion.div>
      </div>
    );
  }

  // Error / expired / timeout
  return (
    <div className="min-h-screen bg-[#0f1419] pt-24 pb-16 flex items-center justify-center">
      <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="max-w-md mx-auto px-4 text-center">
        <div className="w-20 h-20 bg-rose-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
          <XCircle className="w-10 h-10 text-rose-400" />
        </div>
        <h1 className="text-3xl font-bold text-white mb-4 font-outfit" data-testid="payment-failed">
          {status === 'timeout' ? t('checkout.verificationExpired') : t('checkout.paymentFailed')}
        </h1>
        <p className="text-slate-400 mb-8">
          {status === 'timeout'
            ? t('checkout.verificationExpiredDesc')
            : t('checkout.paymentFailedDesc')}
        </p>
        <button
          onClick={onBack}
          className="bg-teal-500 hover:bg-teal-400 text-black font-bold px-8 py-4 rounded-full transition-all"
          data-testid="retry-payment-button"
        >
          {t('checkout.backToCheckout')}
        </button>
      </motion.div>
    </div>
  );
}

export function OrderSuccess({ orderData, onContinue }) {
  const { t } = useTranslation();
  const { formatPrice } = useCurrency();

  return (
    <div className="min-h-screen bg-[#0f1419] pt-24 pb-16 flex items-center justify-center">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="max-w-md mx-auto px-4 text-center"
      >
        <div className="w-20 h-20 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
          <CheckCircle className="w-10 h-10 text-emerald-400" />
        </div>
        <h1 className="text-3xl font-bold text-white mb-4 font-outfit">{t('common.success')}</h1>
        <p className="text-slate-400 mb-8">
          {t('checkout.orderNumber')} <span className="text-teal-400 font-mono">#{orderData?.order_id}</span> - {t('checkout.orderPlaced')}
        </p>
        <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 mb-8">
          <p className="text-slate-400 text-sm mb-2">{t('checkout.orderTotal')}</p>
          <p className="text-3xl font-bold text-white font-mono">{formatPrice(orderData?.total || 0)}</p>
        </div>
        <p className="text-slate-500 text-sm mb-8">{t('checkout.receiveQR')}</p>
        <button
          onClick={onContinue}
          className="bg-teal-500 hover:bg-teal-400 text-black font-bold px-8 py-4 rounded-full transition-all duration-200 hover:shadow-[0_0_20px_rgba(6,182,212,0.5)]"
          data-testid="continue-shopping-button"
        >
          {t('cart.continueShopping')}
        </button>
      </motion.div>
    </div>
  );
}
